import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { VolumeCalculator } from "./VolumeCalculator";

const TIERS = [
  {
    name: "Core",
    price: "$299",
    unit: "per property / quarter",
    badge: "Standard Portfolio",
    features: [
      "TRAIN full onboarding suite",
      "WALK digital inspections & photos",
      "AUDIT basic scoring",
      "Up to 500 units / pads per property",
      "Email & knowledge-base support",
    ],
    cta: "Get Started with Core",
    testid: "pricing-core-cta",
  },
  {
    name: "Pro",
    price: "$499",
    unit: "per property / quarter",
    badge: "Most Popular",
    highlighted: true,
    features: [
      "Everything in Core",
      "Custom SOP & quiz builder",
      "Priority regional onboarding specialist",
    ],
    cta: "Start 14-Day Pro Trial",
    testid: "pricing-pro-cta",
  },
  {
    name: "Volume",
    price: "$199",
    unit: "per community / quarter — 30+ communities",
    badge: "Larger Operators",
    features: [
      "Full OPERA360 platform access",
      "Best per-community rate for 30+ communities",
      "Full access to your own training backpacks",
      "Training customized specifically for your teams",
      "Custom portal or hub — or embed OPERA360 into yours",
    ],
    cta: "Ask for Volume Pricing",
    testid: "pricing-volume-cta",
  },
];

export const Pricing = () => (
  <section id="pricing" className="relative py-24 sm:py-32 border-t border-slate-200 bg-[#E2EDF8]" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl mb-14"
      >
        <p className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-cadmium" /> Pricing
        </p>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
          Priced per property. <span className="font-editorial italic font-normal text-sky-600">Built for portfolios.</span>
        </h2>
        <p className="mt-4 text-sm text-muted-foreground" data-testid="pricing-billing-note">
          All plans are billed quarterly. Larger companies with more than 30 communities start at <span className="font-semibold text-slate-900">$199 / community per quarter</span>.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {TIERS.map((t, i) => (
          <motion.div
            key={t.name}
            data-testid={`pricing-tier-${t.name.toLowerCase()}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className={`relative rounded-md p-7 sm:p-8 flex flex-col border transition-all duration-300 ${
              t.highlighted
                ? "bg-gradient-to-b from-white to-[#E2EDF8] border-cadmium/60 shadow-[0_0_60px_-15px_rgba(255,85,46,0.35)]"
                : "bg-white border-slate-200 hover:border-slate-300"
            }`}
          >
            <span
              className={`absolute -top-3 left-6 rounded-sm px-2.5 py-1 text-[10px] font-mono font-semibold uppercase tracking-widest ${
                t.highlighted ? "bg-cadmium text-white" : "bg-slate-100 border border-slate-200 text-muted-foreground"
              }`}
            >
              {t.badge}
            </span>
            <h3 className="font-cabinet text-xl font-bold text-slate-900 mt-2">{t.name}</h3>
            <p className="mt-4 font-mono text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight">{t.price}</p>
            <p className="mt-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">{t.unit}</p>
            <ul className="mt-7 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-900/85">
                  <Check size={15} className={`mt-0.5 shrink-0 ${t.highlighted ? "text-sky-600" : "text-green-700"}`} />
                  {f}
                </li>
              ))}
            </ul>
            <a
              href="#demo"
              data-testid={t.testid}
              className={`mt-8 inline-flex items-center justify-center rounded-md px-5 py-3 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${
                t.highlighted
                  ? "bg-cadmium text-white hover:bg-cadmium-hover"
                  : "border border-slate-300 text-slate-900 hover:border-cadmium/60 hover:text-sky-600"
              }`}
            >
              {t.cta}
            </a>
          </motion.div>
        ))}
      </div>

      <VolumeCalculator />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        data-testid="pricing-custom-callout"
        className="mt-8 rounded-md border border-slate-200 bg-white/70 backdrop-blur p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
      >
        <div className="flex-1">
          <p className="eyebrow mb-2 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> Built Around Your Team
          </p>
          <p className="text-sm sm:text-base text-slate-900/85 leading-relaxed">
            Every company gets full access to their own training backpacks, and we customize the training specifically for your teams.
            We can build you a dedicated <span className="font-semibold text-slate-900">custom portal or hub</span> — or embed OPERA360 straight into your existing one.
          </p>
        </div>
        <a
          href="#demo"
          data-testid="pricing-custom-cta"
          className="shrink-0 inline-flex items-center justify-center rounded-md bg-cadmium px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5 active:scale-[0.98]"
        >
          Ask for Pricing
        </a>
      </motion.div>
    </div>
  </section>
);
